<template>
  <!-- Global loading overlay, driven by the store's isLoading flag (setIsLoading). -->
  <div class="ff-loading" v-bind:class="{'is-hidden':!$store.state.isLoading}" role="status" aria-live="polite">
    <div class="ff-loading-card">
      <span class="ff-spinner"></span>
      <span>{{ $t('common.loading') }}</span>
    </div>
  </div>
  <div id="wrapper" class="main-layout ff-shell" :class="{ 'ff-shell--nav-hidden': navHidden && ui.nav === 'side' }">
    <!-- Sidebar in the FitFoodee console style: logo and the settings gear at the top, the
         grouped links below. With the menu on top (user menu > Menu) the same markup is the bar
         across the top, laid out by the :root[data-ff-nav="top"] rules. .github/workflows/deploy-ann.yml seds the nav's class string and the
         logo path below (ann: the sidebar fades into the theme colour from 35% of its height, ANN
         logo) - keep both verbatim. InBill.vue hides the sidebar via #navbar-menu's parent, so
         #navbar-menu stays a direct child of <nav>. -->
    <nav class="navbar is-danger ff-nav" v-show="isLoggedIn" :class="{ 'is-fit-1': navFit >= 1, 'is-fit-2': navFit >= 2 }">
      <div class="ff-nav-inner" id="navbar-menu">
        <div class="ff-nav-head">
          <div class="ff-brand">
            <img class="logo" src="@/assets/ann_profile.jpg" alt="" >
          </div>
          <user-menu @logout="logout" />
        </div>

        <!-- theo thứ tự tạo bên file route.js -->
        <div class="ff-links" ref="navLinks">
          <div v-for="group in barGroups" :key="group.title" class="ff-nav-group">
            <p class="ff-nav-section">{{ group.title }}</p>
            <router-link v-for="item in group.items" :key="item.to" :to="item.to" class="ff-link">
              <svg class="ff-nav-icon" viewBox="0 0 24 24" v-html="item.icon"></svg>
              <span>{{ item.label }}</span>
            </router-link>
          </div>
        </div>

        <!-- menu on top: the Settings group is one icon whose links drop down on hover; on one of
             its pages the icon carries that page's icon and name. Outside .ff-links, which
             scrolls sideways and would clip the list. -->
        <!-- a click on a link closes the list until the pointer leaves it (is-closed) -->
        <div v-if="dropGroup" class="ff-nav-drop" :class="{ 'is-closed': dropClosed }" @mouseleave="dropClosed = false">
          <button type="button" class="ff-link ff-nav-drop-btn" :class="{ 'is-active': dropActive }" :title="dropGroup.title" @click="dropClosed = false">
            <svg class="ff-nav-icon" viewBox="0 0 24 24" v-html="dropActive ? dropActive.icon : NAV_DROP_ICON"></svg>
            <span v-if="dropActive">{{ dropActive.label }}</span>
            <svg class="ff-nav-caret" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
          </button>
          <div class="ff-nav-drop-menu">
            <div class="ff-nav-drop-list">
              <router-link v-for="item in dropGroup.items" :key="item.to" :to="item.to" class="ff-link" @click="closeDrop">
                <svg class="ff-nav-icon" viewBox="0 0 24 24" v-html="item.icon"></svg>
                <span>{{ item.label }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- hide / show the sidebar: a strip on its right edge, at the screen's left edge while hidden -->
      <button v-if="ui.nav === 'side'" class="ff-nav-toggle" type="button" :title="navHidden ? 'Show menu' : 'Hide menu'" @click="toggleNav">
        <svg viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" /></svg>
      </button>
    </nav>
    <section class="section m-0	p-0 is-flex is-flex-direction-column main-layout" >
      <div class="container is-fluid m-0 p-0 is-flex main-layout"  >
        <router-view>

        </router-view>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

import { ref } from 'vue'
import UserMenu from '@/components/UserMenu.vue'
import { syncUiPrefsFromServer, uiState } from '@/helpers/uiPrefs.js'
// the Bank Receipts page is Fitfood's: deploy-ann.yml turns it off there
import { BANK_RECEIPTS_ON } from '@/helpers/features.js'

// Sidebar link icons: the inner markup of 24x24 stroke icons (Lucide), drawn in the link's colour
const NAV_ICONS = {
  csnote: '<path d="M15.5 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V8.5L15.5 3Z"/><path d="M15 3v6h6"/>',
  orderhistory: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/>',
  inship: '<path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/>',
  shippingtool: '<circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/>',
  bankreceipt: '<path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 17.5v-11"/>',
  dataload: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m17 8-5-5-5 5"/><path d="M12 3v12"/>',
  controlpage: '<path d="M21 4h-7"/><path d="M10 4H3"/><path d="M21 12h-9"/><path d="M8 12H3"/><path d="M21 20h-5"/><path d="M12 20H3"/><path d="M14 2v4"/><path d="M8 10v4"/><path d="M16 18v4"/>',
  scheduledjobs: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  settings: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/>',
}
// the Settings group's icon in the bar across the top (Lucide wrench)
const NAV_DROP_ICON = '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>'

// the sidebar's hidden / shown choice, kept in this browser
const NAV_HIDDEN_KEY = 'ffNavHidden'

function readNavHidden () {
  try { return localStorage.getItem(NAV_HIDDEN_KEY) === '1' } catch (e) { return false }
}

export default {
  components: { UserMenu },

  data() {
    return {
      bankReceiptsOn: BANK_RECEIPTS_ON,
      NAV_DROP_ICON,
      // the Settings list was just used: kept shut while the pointer is still over it
      dropClosed: false,
      localIsPlanning: false,
      localIsCashAccountant: false,
      localIsAuthen: false,
      navHidden: readNavHidden(),
      ui: uiState,
      // the bar across the top, when its links would not fit: 1 drops the user name, 2 tightens
      // the links
      navFit: 0,
    }
  },

  async beforeCreate() {

    await this.$store.commit('initializeStore')

    if (this.$store.getters.isAuthenticated) {
      // the user's display settings from the server (the copy here painted the page meanwhile)
      syncUiPrefsFromServer()
      // this is to check token current validity
      await axios
        .get('/api/v1/user/?username=' + localStorage.getItem('user'))
        .then(response => {
          localStorage.setItem('userGroups', response.data['groups'])
          response.data['groups'].includes(1) ? this.$store.commit('setIsPlaning', true) : this.$store.commit('setIsPlaning', false)
          response.data['groups'].includes(3) ? this.$store.commit('setIsCashAccountant', true) : this.$store.commit('setIsCashAccountant', false)
        })
        .catch(error => {          
          if (error.response.status == 401) {
            this.$store.dispatch('LogOut')
            this.$router.push('/login')
          }
        })  

    } else {
      
    }

    
    // var width = document.documentElement.clientWidth * window.devicePixelRatio;
    // let viewport = document.querySelector("meta[name=viewport]");
    // viewport.setAttribute('content', 'width=' + width + ', minimum-scale= 1');
    // document.documentElement.style.transform = `scale(${1 / window.devicePixelRatio})`;
    // document.documentElement.style.transformOrigin = 'top left';

  },

  async beforeMount() {
    // console.log('beforeMount APP')
    try {
      localStorage.getItem('userGroups').split(',').includes('1') ? this.$store.commit('setIsPlaning', true) : this.$store.commit('setIsPlaning', false)
      localStorage.getItem('userGroups').split(',').includes('3') ? this.$store.commit('setIsCashAccountant', true) : this.$store.commit('setIsCashAccountant', false)

      localStorage.getItem('userGroups').split(',').includes('1') ? this.localIsPlanning = true : this.localIsPlanning = false
      localStorage.getItem('userGroups').split(',').includes('3') ? this.localIsCashAccountant = true : this.localIsCashAccountant = false

    } catch (error) {  1 == 1 }
    
    try { localStorage.getItem('token') ? (this.localIsAuthen=true) : (1==1) } catch (error) { 1 == 1 }
    
  },

  mounted() {
    window.addEventListener('resize', this.fitTopNav)
    // the links' width changes once the web font arrives
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.fitTopNav)
    this.fitTopNav()

    // browser tab icon = the nav logo. deploy-ann.yml rewrites this path like the <img> above, so
    // the ann build shows the ANN logo in the tab too.
    const tabIcon = document.querySelector("link[rel~='icon']") || document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'icon' }))
    tabIcon.href = require('@/assets/ann_profile.jpg')

    // if (this.localIsAuthen) {
    //   axios
    //     .get('/api/v1/user/?username=' + localStorage.getItem('user'))
    //     .then(response => {
    //       localStorage.setItem('userGroups', response.data['groups'])
    //       response.data['groups'].includes(1) ? this.$store.commit('setIsPlaning', true) : this.$store.commit('setIsPlaning', false)
    //       response.data['groups'].includes(3) ? this.$store.commit('setIsCashAccountant', true) : this.$store.commit('setIsCashAccountant', false)
          

    //       // localStorage.getItem('userGroups').split(',').includes('1') ? this.localIsPlanning = true : this.localIsPlanning = false
    //       // localStorage.getItem('userGroups').split(',').includes('3') ? this.localIsCashAccountant = true : this.localIsCashAccountant = false

    //     })
    //     .catch(error => {          
    //       if (error.response.status == 401) {
    //         this.$store.dispatch('LogOut')
    //         this.$router.push('/login')
    //       }
    //     })  

    // } else {
      
    // }
    

  },

  updated() {
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.fitTopNav)
  },

  watch: {
    // what changes the top bar's links or their width
    'ui.nav' () { this.fitTopNav() },
    'ui.font' () { this.fitTopNav() },
    isLoggedIn () { this.fitTopNav() },
    navGroups () { this.fitTopNav() },
    // the Settings icon takes the name of the Settings page that opens
    '$route.path' () { this.fitTopNav() },
  },

  

  computed : {
      isLoggedIn : function(){ 
      
        return this.$store.getters.isAuthenticated
      },
      
      isPlaning : function(){ 
       
        return this.$store.getters.isPlaning
      },

      isCashAccountant : function(){ 
       
       return this.$store.getters.isCashAccountant
     },

      // the sidebar's links by section, each shown to the groups it was shown to in the old top bar
      navGroups () {
        const planning = this.isPlaning || this.localIsPlanning
        const cash = this.isCashAccountant || this.localIsCashAccountant
        const link = (to, label, show = true) => show ? [{ to: '/' + to, label, icon: NAV_ICONS[to] }] : []
        return [
          { title: 'Customers', items: [
            ...link('csnote', 'CS Notes'),
            ...link('orderhistory', 'Order Hist'),
            ...link('inship', 'In Ship', !cash || (this.isCashAccountant && this.isPlaning)),
          ] },
          { title: 'Planning', items: [
            ...link('dataload', 'Data Load', planning || cash),
            ...link('controlpage', 'Control Page', planning || cash),
            ...link('shippingtool', 'Shipping Tool', planning || cash),
            ...link('bankreceipt', 'Bank Receipts', this.bankReceiptsOn && (planning || cash)),
          ] },
          // dropdown: with the menu on top, one icon whose links drop down on hover
          { title: 'Settings', dropdown: true, items: [
            ...link('scheduledjobs', 'Scheduled Jobs', planning || cash),
            ...link('settings', 'Settings', planning),
          ] },
        ].filter(group => group.items.length)
      },

      // the groups laid out as links: all of them in the sidebar, all but the dropdown on top
      barGroups () {
        return this.ui.nav === 'top' ? this.navGroups.filter(group => !group.dropdown) : this.navGroups
      },

      dropGroup () {
        return this.ui.nav === 'top' ? this.navGroups.find(group => group.dropdown) || null : null
      },

      // the dropdown's page that is open, if any
      dropActive () {
        const path = this.$route.path
        return this.dropGroup ? this.dropGroup.items.find(item => path === item.to || path.startsWith(item.to + '/')) || null : null
      },

      // testComputed: function() {
      //   return this.$store.getters.activeURL
      // }
      
  },

  methods: {

    async logout () {
      await this.$store.dispatch('LogOut')
      this.$router.push('/login')
      window.location.reload()
    },

    // the bar across the top: the fullest look whose links fit in the bar
    async fitTopNav () {
      if (this.ui.nav !== 'top') { this.navFit = 0; return }
      for (let level = 0; level <= 2; level++) {
        this.navFit = level
        await this.$nextTick()
        const links = this.$refs.navLinks
        if (!links || links.scrollWidth <= links.clientWidth) return
      }
    },

    // a Settings page was picked: shut the list, which hover and focus would otherwise keep open
    closeDrop () {
      this.dropClosed = true
      if (document.activeElement) document.activeElement.blur()
    },

    toggleNav () {
      this.navHidden = !this.navHidden
      try { localStorage.setItem(NAV_HIDDEN_KEY, this.navHidden ? '1' : '0') } catch (e) { /* kept for this visit only */ }
      // the pages just changed width: let the ones that measure themselves do it again
      this.$nextTick(() => window.dispatchEvent(new Event('resize')))
    },


    

  }
}

</script>

<style lang="scss">
@import '../node_modules/bulma/'

</style>
<style>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700;800&display=swap');
</style>
<style lang="scss">


#wrapper.main-layout {
  overflow-y: hidden; 
  height: 100vh; 
  align-items: stretch;
}
// Display settings from the user menu (helpers/uiPrefs.js) land on <html> as
// data-ff-theme / data-ff-font / data-ff-style. Only the new-style surfaces (top nav,
// Order Hist 2, CS Notes, the ffc pages) read these --ff-* variables; the Bulma pages never do.
// :root is the standard look (Style off): white panels on a light grey page.
:root {
  --ff-brand: #1f7a4d;
  --ff-brand-ink: #155f3b;
  --ff-paper: #eef1f4;
  --ff-hairline: #e7e3da;
  --ff-ink: #1d211e;
  --ff-muted: #6b716c;
  --ff-scale: 1;
  /* the bar's height with the menu on top; the pages are the screen height less this */
  --ff-nav-h: 0px;
  /* cards, panels, inputs, nav */
  --ff-surface: #ffffff;
}

/* Colour theme (user menu): brand, its darker ink for text / hover, and a hairline
   tinted to match. Herb is the :root default above; helpers/uiPrefs.js THEMES lists
   the same swatches. Custom sets --ff-brand inline and derives the rest from it. */
:root[data-ff-theme="slate"] {
  --ff-brand: #2d5fa6;
  --ff-brand-ink: #224a83;
  --ff-hairline: #dfe4ec;
}

/* Plum: the ANN logo purple, the ann build's default theme (helpers/uiPrefs.js DEFAULT_THEME) */
:root[data-ff-theme="plum"] {
  --ff-brand: #5e3f96;
  --ff-brand-ink: #48307a;
  --ff-hairline: #e7e1ef;
}

:root[data-ff-theme="custom"] {
  --ff-brand-ink: color-mix(in srgb, var(--ff-brand) 78%, #000000);
  --ff-hairline: color-mix(in srgb, var(--ff-brand) 12%, #e7e3da);
}

:root[data-ff-font="s"] { --ff-scale: 0.9; }
:root[data-ff-font="l"] { --ff-scale: 1.1; }
:root[data-ff-font="xl"] { --ff-scale: 1.22; }

/* Style (user menu): a preset from helpers/stylePresets.json or the user's own colours, set
   inline on <html> by helpers/uiPrefs.js as --ff-paper/-surface/-ink/-muted/-hairline and
   --ff-line (the 3:1 outline of panels and controls; pages fall back to the hairline without
   it). .claude/skills/ui-comfort/scripts/contrast.mjs checks every preset. */

// Less motion, with a style on or when the system asks for it ("reduce motion"): no fades or
// slides, and scrolls jump instead of gliding (helpers/uiPrefs.js calmMotion does the same for
// scripted scrolls). The loading spinner keeps turning, so a wait still shows.
@mixin ff-calm {
  transition-duration: 0s !important;
  transition-delay: 0s !important;
  scroll-behavior: auto !important;
}

html[data-ff-style] *,
html[data-ff-style] *::before,
html[data-ff-style] *::after { @include ff-calm; }

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after { @include ff-calm; }
}

// Checkboxes on the new-style pages, drawn from the tokens. The browser paints an empty native
// box pure white with its own grey edge whatever the page colours (accent-color only reaches
// the ticked state), which shows as a bright hole on a style's matte panels. On the standard
// white panels this looks as before: white box, mid-grey edge (about 4:1), brand tick.
// Switches draw themselves; Vuetify keeps its own hidden inputs.
:is(.oh2, .ffc-page, .csn) input[type="checkbox"]:not([class*="switch"]):not(.v-selection-control input) {
  -webkit-appearance: none;
  appearance: none;
  display: inline-grid;
  place-content: center;
  border: 1.5px solid color-mix(in srgb, var(--ff-ink) 60%, var(--ff-surface));
  border-radius: 4px;
  background: var(--ff-surface);
  cursor: pointer;

  // the tick (a clip-path check mark) or, half-ticked, a dash
  &::before {
    content: '';
    width: 0.72em;
    height: 0.72em;
    background: #ffffff;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0, 43% 62%);
    transform: scale(0);
  }

  &:checked,
  &:indeterminate { border-color: var(--ff-brand); background: var(--ff-brand); }
  &:checked::before { transform: scale(1); }
  &:indeterminate::before { height: 2px; clip-path: none; transform: scale(1); }
  &:hover:not(:disabled) { border-color: var(--ff-brand); }
  &:focus-visible { outline: 2px solid color-mix(in srgb, var(--ff-brand) 50%, transparent); outline-offset: 1px; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }

  // Windows high-contrast mode: the tick takes the system text colour
  @media (forced-colors: active) {
    &::before { background: CanvasText; }
  }
}

// Sidebar in the FitFoodee console style (Noto Sans, theme from the user menu): logo and the
// settings gear at the top, the grouped links below, scrolling on their own. The pages fill the
// full screen height to its right; the toggle strip on its edge hides it to give them the width.
.ff-shell {
  display: flex;
  flex-direction: row;
  position: relative;
}

nav.navbar.ff-nav {
  --nav-brand: var(--ff-brand);
  --nav-brand-ink: var(--ff-brand-ink);
  --nav-ink: var(--ff-ink);
  --nav-muted: var(--ff-muted);
  --nav-hairline: var(--ff-hairline);
  --nav-bg: var(--ff-surface);
  --nav-link: var(--nav-ink);
  --nav-hover-bg: color-mix(in srgb, var(--nav-brand) 7%, transparent);
  --nav-active-bg: color-mix(in srgb, var(--nav-brand) 13%, transparent);
  --nav-active-ink: var(--nav-brand-ink);
  --nav-avatar-bg: var(--nav-brand);
  --nav-avatar-ink: #ffffff;
  --nav-line: var(--ff-line, var(--nav-hairline));

  flex: none;
  position: relative;
  z-index: 30;
  width: 232px;
  height: 100vh;
  min-height: 0;
  display: block;
  background-color: var(--nav-bg);
  border-right: 1px solid var(--nav-line);
  color: var(--nav-ink);
  font-family: 'Noto Sans', system-ui, -apple-system, sans-serif;
  font-feature-settings: 'tnum' 1;
}

// hidden: no width, only the toggle strip at the screen's left edge
.ff-shell--nav-hidden nav.navbar.ff-nav {
  width: 0;
  border-right: 0;

  .ff-nav-inner { display: none; }
}

// ann build: deploy-ann.yml turns the nav's is-dark into is-danger. The sidebar itself is the same
// as on fitfood; what marks ann is it fading into the theme colour from 35% of its height (Plum,
// the ANN purple, by default there). 58% strength at the bottom: the darkest that keeps the text
// on it at 4.5:1 or more with every theme and style (5.7:1 on plain Plum).
nav.navbar.ff-nav.is-danger {
  // the nav's own colours, not Bulma's red is-danger bar
  background-color: var(--nav-bg) !important;

  .ff-nav-inner {
    background: linear-gradient(to bottom, transparent 35%, color-mix(in srgb, var(--nav-brand) 58%, transparent));
  }
}

.ff-nav-inner {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ff-nav-head {
  flex: none;
  // above the toggle strip, so the gear stays clickable; anchors the settings panel
  position: relative;
  z-index: 13;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 11px 14px;
  border-bottom: 1px solid var(--nav-hairline);
}

.ff-brand {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
}

.ff-brand .logo {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
}



.ff-links {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 10px 20px;
  scrollbar-width: thin;
}

.ff-nav-group { margin-top: 14px; }

.ff-nav-section {
  margin: 0 0 4px;
  padding: 0 10px;
  color: var(--nav-muted);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ff-link {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1px;
  padding: 8px 10px;
  border-radius: 9px;
  color: var(--nav-link) !important;
  font-size: calc(14px * var(--ff-scale));
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  transition: background 0.12s ease, color 0.12s ease;
}

.ff-link:hover {
  background: var(--nav-hover-bg);
  color: var(--nav-ink) !important;
}

.ff-link.router-link-exact-active,
.ff-link.router-link-active:not([href="/"]) {
  background: var(--nav-active-bg);
  color: var(--nav-active-ink) !important;
  font-weight: 700;
}

.ff-nav-icon {
  flex: none;
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

// hide / show strip: on the sidebar's right edge, faint until hovered
.ff-nav-toggle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(100% - 26px);
  z-index: 12;
  width: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--nav-muted);
  cursor: pointer;
  opacity: 0.35;
  transition: color 0.12s ease, opacity 0.12s ease;

  svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    opacity: 1;
    color: var(--nav-brand-ink);
    background: linear-gradient(to left, color-mix(in srgb, var(--nav-brand) 16%, var(--nav-bg)) 12%, transparent);
  }
}

.ff-shell--nav-hidden .ff-nav-toggle {
  left: 0;
  width: 28px;

  svg { transform: rotate(180deg); }

  &:hover {
    background: linear-gradient(to right, color-mix(in srgb, var(--nav-brand) 16%, var(--nav-bg)) 12%, transparent);
  }
}

// Menu on top (user menu > Menu): the same nav as a bar across the top, in the sidebar's style -
// the logo, the links (text only) in groups split by a short divider, the active one in the same
// tinted pill, and the user's name and the gear on the right. When the links would not fit
// (fitTopNav), the name goes, then the links pack closer (is-fit-1, is-fit-2). The section titles,
// the icons and the hide strip belong to the sidebar only. Ann's fade runs left to right here.
:root[data-ff-nav="top"] {
  --ff-nav-h: max(5vh, 3.25rem);

  .ff-shell { flex-direction: column; }

  nav.navbar.ff-nav {
    width: auto;
    height: var(--ff-nav-h);
    border-right: 0;
    border-bottom: 1px solid var(--nav-line);
    box-shadow: 0 1px 3px rgba(29, 33, 30, 0.05);
  }

  nav.navbar.ff-nav.is-danger .ff-nav-inner {
    background: linear-gradient(to right, transparent 35%, color-mix(in srgb, var(--nav-brand) 58%, transparent));
  }

  .ff-nav-inner {
    flex-direction: row;
    align-items: center;
    padding: 0 12px 0 14px;
  }

  // the brand and the gear become items of the bar; the gear goes last
  .ff-nav-head { display: contents; }
  .ff-brand { flex: none; }
  .ff-brand .logo { width: 30px; height: 30px; }

  // short dividers: after the brand and between the link groups
  .ff-brand::after,
  .ff-nav-group + .ff-nav-group::before {
    content: '';
    flex: none;
    width: 1px;
    height: 22px;
    background: var(--nav-line);
  }

  .ff-brand::after { margin: 0 10px 0 5px; }
  .ff-nav-group + .ff-nav-group::before { margin: 0 8px; }

  .ff-links {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    padding: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }

  .ff-links::-webkit-scrollbar { display: none; }

  .ff-nav-group {
    flex: none;
    display: flex;
    align-items: center;
    gap: 2px;
    margin: 0;
  }

  .ff-nav-section,
  .ff-nav-icon { display: none; }
  .ff-link { flex: none; margin: 0; padding: 7px 12px; }

  .ff-user-wrap { flex: none; order: 1; position: relative; margin-left: 10px; }
  .ff-menu { top: calc(100% + 10px); left: auto; right: 0; }

  nav.ff-nav.is-fit-1 .ff-nav-user { display: none; }
  nav.ff-nav.is-fit-2 .ff-link { padding: 7px 8px; }
  nav.ff-nav.is-fit-2 .ff-nav-group + .ff-nav-group::before { margin: 0 4px; }

  // the Settings group: one icon (plus the open page's name) after the links, its list dropping
  // down on hover, or on focus for the keyboard and touch screens
  .ff-nav-drop {
    flex: none;
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
  }

  .ff-nav-drop::before {
    content: '';
    flex: none;
    width: 1px;
    height: 22px;
    margin: 0 8px;
    background: var(--nav-line);
  }

  .ff-nav-drop .ff-nav-icon { display: block; }

  .ff-nav-drop-btn {
    gap: 7px;
    border: 0;
    background: transparent;
    font-family: inherit;
    cursor: pointer;
  }

  .ff-nav-drop-btn.is-active {
    background: var(--nav-active-bg);
    color: var(--nav-active-ink) !important;
    font-weight: 700;
  }

  .ff-nav-caret {
    flex: none;
    width: 14px;
    height: 14px;
    margin-left: -2px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  // padding-top bridges the gap, so the list stays open while the pointer moves down to it
  .ff-nav-drop-menu {
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 40;
    padding-top: 4px;
  }

  .ff-nav-drop:hover .ff-nav-drop-menu,
  .ff-nav-drop:focus-within .ff-nav-drop-menu { display: block; }
  .ff-nav-drop.is-closed .ff-nav-drop-menu { display: none; }

  .ff-nav-drop-list {
    min-width: 200px;
    padding: 6px;
    background: var(--nav-bg);
    border: 1px solid var(--nav-line);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(29, 33, 30, 0.12);
  }

  .ff-nav-drop-list .ff-link { gap: 10px; padding: 8px 10px; margin-bottom: 1px; }
  nav.ff-nav.is-fit-2 .ff-nav-drop::before { margin: 0 4px; }
}

// the user's name and the settings gear (components/UserMenu.vue); the panel is anchored to the
// sidebar's head
.ff-user-wrap {
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ff-nav-user {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--nav-ink);
  font-size: calc(14px * var(--ff-scale));
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}

.ff-gear { flex: none; }

.ff-gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--nav-line);
  border-radius: 9px;
  background: var(--nav-bg);
  color: var(--nav-muted);
  cursor: pointer;
  transition: color 0.12s ease, background 0.12s ease;

  svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover,
  &.is-open {
    color: var(--nav-brand-ink);
    background: color-mix(in srgb, var(--nav-brand) 10%, var(--nav-bg));
  }
}

.ff-menu-account {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--ff-hairline);
  font-size: 13.5px;
  font-weight: 700;
}

.ff-menu-account .ff-user-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ff-avatar {
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: var(--ff-brand);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
}

.ff-btn {
  flex: none;
  padding: 5px 12px;
  border: 1px solid var(--ff-line, var(--ff-hairline));
  border-radius: 8px;
  background: transparent;
  color: var(--ff-ink) !important;
  font-family: inherit;
  font-size: calc(12.5px * var(--ff-scale));
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s ease;
}

.ff-btn:hover { background: color-mix(in srgb, var(--ff-brand) 7%, transparent); }

// ---- global loading overlay: soft paper veil + a small card with a spinner.
// Fades in after 150ms so quick requests don't flash; sits above modals (2980).
.ff-loading {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--ff-paper) 55%, transparent);
  backdrop-filter: blur(2px);
  animation: ff-loading-in 0.2s ease 0.15s both;
}

.ff-loading-card {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border: 1px solid var(--ff-line, var(--ff-hairline));
  border-radius: 14px;
  background: var(--ff-surface);
  box-shadow: 0 14px 30px -12px rgba(29, 33, 30, 0.25), 0 1px 2px rgba(29, 33, 30, 0.06);
  color: var(--ff-ink);
  font-family: 'Noto Sans', system-ui, -apple-system, sans-serif;
  font-size: calc(14px * var(--ff-scale));
  font-weight: 700;
}

.ff-spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid color-mix(in srgb, var(--ff-brand) 18%, transparent);
  border-top-color: var(--ff-brand);
  border-radius: 50%;
  animation: ff-spin 0.8s linear infinite;
}

@keyframes ff-spin { to { transform: rotate(360deg); } }
@keyframes ff-loading-in { from { opacity: 0; } to { opacity: 1; } }

// App-wide tooltip bubble for every `title` (helpers/tooltip.js places it).
.ff-tip {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5000;
  max-width: min(320px, calc(100vw - 12px));
  padding: 6px 10px;
  border-radius: 7px;
  background: var(--ff-ink);
  color: #ffffff;
  font-family: 'Noto Sans', system-ui, -apple-system, sans-serif;
  font-size: calc(12px * var(--ff-scale, 1));
  font-weight: 500;
  line-height: 1.45;
  letter-spacing: 0.01em;
  white-space: pre-line;
  overflow-wrap: anywhere;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0, 0, 0, 0.12);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.12s ease, visibility 0s linear 0.12s;
}

.ff-tip.is-on {
  opacity: 1;
  visibility: visible;
  transition: opacity 0.12s ease, visibility 0s;
}

.ff-tip::after {
  content: '';
  position: absolute;
  left: var(--ff-tip-arrow, 50%);
  width: 8px;
  height: 8px;
  background: inherit;
  transform: translateX(-50%) rotate(45deg);
}

.ff-tip[data-side="below"]::after { top: -4px; }
.ff-tip[data-side="above"]::after { bottom: -4px; }

// user menu: the page's surface and theme colours, on either build
.ff-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 8px;
  z-index: 40;
  width: 300px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
  background: var(--ff-surface);
  border: 1px solid var(--ff-line, var(--ff-hairline));
  border-radius: 12px;
  box-shadow: 0 14px 26px -14px rgba(29, 33, 30, 0.32), 0 1px 2px rgba(29, 33, 30, 0.06);
  color: var(--ff-ink);
  font-size: 13px;
  text-align: left;
}

.ff-menu-label {
  margin-bottom: 7px;
  color: var(--ff-muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.ff-seg {
  display: flex;
  border: 1px solid var(--ff-line, var(--ff-hairline));
  border-radius: 8px;
  overflow: hidden;
}

.ff-seg button {
  flex: 1;
  height: 32px;
  border: 0;
  background: var(--ff-surface);
  color: var(--ff-muted);
  font-family: inherit;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
}

.ff-seg button + button { border-left: 1px solid var(--ff-hairline); }
.ff-seg button:hover { background: color-mix(in srgb, var(--ff-brand) 8%, var(--ff-surface)); }

.ff-seg button.is-on {
  background: color-mix(in srgb, var(--ff-brand) 14%, var(--ff-surface));
  color: var(--ff-brand-ink);
}

.ff-swatches { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }

.ff-swatch {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 4px;
  border: 1px solid var(--ff-line, var(--ff-hairline));
  border-radius: 8px;
  background: var(--ff-surface);
  color: var(--ff-ink);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.ff-swatch:hover { border-color: color-mix(in srgb, var(--ff-ink) 25%, var(--ff-hairline)); }

.ff-swatch.is-on {
  border-color: var(--ff-brand);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ff-brand) 20%, transparent);
}

.ff-swatch-dot { width: 12px; height: 12px; border-radius: 50%; }
.ff-swatch-bg { box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18); }

// the custom dot is the colour input itself: a transparent native picker laid over it
.ff-swatch-picker { position: relative; overflow: hidden; box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12); }

.ff-swatch-picker input[type="color"] {
  position: absolute;
  inset: -6px;
  width: calc(100% + 12px);
  height: calc(100% + 12px);
  opacity: 0;
  cursor: pointer;
}

.ff-swatch-custom { position: relative; }

.ff-menu-note { color: var(--ff-muted); font-size: 11.5px; }
.ff-menu-hint { margin-top: 6px; color: var(--ff-muted); font-size: 11.5px; line-height: 1.4; }

// a section label with its on / off switch on the right
.ff-menu-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 7px; }
.ff-menu-head .ff-menu-label { margin-bottom: 0; }
.ff-seg.ff-seg-sm button { height: 24px; padding: 0 10px; font-size: 11.5px; }

// Style presets: one row per level, a tile per hue showing the page, a panel and its text
.ff-style-row { display: flex; align-items: center; gap: 5px; margin-bottom: 5px; }
.ff-style-level { flex: none; width: 34px; color: var(--ff-muted); font-size: 11px; font-weight: 700; }

.ff-style-tile {
  flex: 1;
  height: 30px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid var(--ff-line, var(--ff-hairline));
  border-radius: 7px;
  cursor: pointer;
}

.ff-style-tile.is-on {
  border-color: var(--ff-brand);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ff-brand) 35%, transparent);
}

.ff-style-panel { padding: 1px 4px; border: 1px solid; border-radius: 4px; font-size: 10px; font-weight: 800; line-height: 1.2; }
.ff-style-current { margin: 2px 0 8px; color: var(--ff-ink); font-size: 12px; font-weight: 700; }

// the four colours of the current style; changing one makes the style the user's own
.ff-style-edit { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }

.ff-style-color {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 5px 7px;
  border: 1px solid var(--ff-line, var(--ff-hairline));
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.ff-style-color > span:nth-child(2) { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ff-ratio { flex: none; color: var(--ff-muted); font-size: 10.5px; font-weight: 700; }
.ff-ratio.is-low { color: #b0281f; }

// the page area: right of the sidebar, the full screen height
section.main-layout {
  flex: 1 1 auto;
  min-width: 0;
  max-height: calc(100vh - var(--ff-nav-h));
  overflow-y: hidden;
}

.container.main-layout {
  flex-grow: 1;
  max-height: calc(100vh - var(--ff-nav-h));
  overflow-y: hidden;
}


input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
}

input[type=number] {
    -moz-appearance:textfield; /* Firefox */
}

::-webkit-scrollbar {
  width: 20px;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

// thumbs from the text colour over the panels, so they show on any style
::-webkit-scrollbar-thumb {
  background-color: color-mix(in srgb, var(--ff-ink) 22%, var(--ff-surface));
  border-radius: 20px;
  border: 6px solid transparent;
  background-clip: content-box;
}

::-webkit-scrollbar-thumb:hover {
  background-color: color-mix(in srgb, var(--ff-ink) 40%, var(--ff-surface));
}

td {
  padding-top: 0.4rem !important;
  padding-bottom: 0.4rem !important;
  font-size:1.1rem;
  height: auto !important;;
  // text-align: center;
}

thead:not(.thead-special-format) th {
  padding-top: 0.4rem !important;
  padding-bottom: 0.4rem !important;
  font-size:1.1rem;
  height: auto !important;;
  // text-align: center;
}

hr {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

</style>

